import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2, Save, ArrowRight, Brain, AlertTriangle, Target, UserCheck } from "lucide-react";
import { stageLabel, translateStageDescription } from "../../lib/stageLabels";

const STAGES = ["New","Researching","Qualified","Contacted","Replied","Assessment Requested","Assessment Delivered","Paid","Repeat Customer","Lost Opportunity"];
const GRADE_BG = { A: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", B: "bg-amber-500/15 text-amber-300 border-amber-500/40", C: "bg-rose-500/15 text-rose-300 border-rose-500/40" };
const PRIORITY_BG = {
  "Immediate":  "bg-rose-500/20 text-rose-200 border-rose-400/60",
  "This Week":  "bg-amber-500/20 text-amber-200 border-amber-400/60",
  "This Month": "bg-sky-500/15 text-sky-300 border-sky-500/40",
  "Nurture":    "bg-slate-500/15 text-slate-300 border-slate-500/40",
};

export default function ProspectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [activities, setActivities] = useState([]);
  const [scoring, setScoring] = useState(false);
  const [intelLoading, setIntelLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [followDate, setFollowDate] = useState("");

  const load = async () => {
    const [pr, ar] = await Promise.all([
      api.get(`/prospects/${id}`),
      api.get(`/prospects/${id}/activities`),
    ]);
    setP(pr.data); setActivities(ar.data);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load prospect")); }, [id]); // eslint-disable-line

  const score = async () => {
    setScoring(true);
    try {
      const r = await api.post(`/prospects/${id}/score`);
      toast.success(`Scored: ${r.data.score}/100 (${r.data.grade})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scoring failed"); }
    finally { setScoring(false); }
  };

  const generateIntel = async () => {
    setIntelLoading(true);
    try {
      const r = await api.post(`/prospects/${id}/intelligence-report`);
      toast.success(`Intelligence Report: ${r.data.total_score}/100 · ${r.data.grade} · ${r.data.priority}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Intelligence Report failed"); }
    finally { setIntelLoading(false); }
  };

  const changeStage = async (stage) => {
    try {
      await api.patch(`/prospects/${id}/stage`, { stage, next_follow_up_date: followDate });
      toast.success(`Moved to ${stageLabel(stage)}`);
      setFollowDate("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Stage change blocked"); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await api.post(`/prospects/${id}/activities`, { activity_type: "note", description: newNote });
    setNewNote(""); await load();
  };

  const remove = async () => {
    if (!window.confirm("Delete this prospect? Activities will also be removed.")) return;
    await api.delete(`/prospects/${id}`);
    toast.success("Deleted");
    nav("/internal/prospects");
  };

  if (!p) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-sky-400 animate-spin" /></div>;

  return (
    <div data-testid="prospect-detail">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="tick-label text-sky-400 mb-2">/ Prospect Intelligence Card</div>
          <h1 className="font-display font-black text-3xl text-slate-100">{p.company_name}</h1>
          <div className="font-mono text-xs text-slate-500 mt-1">
            {p.primary_naics || "NAICS —"} · Source: <span className="text-sky-400">{p.source_type}</span>{p.source_detail ? ` · ${p.source_detail}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={score} disabled={scoring} data-testid="score-btn" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 disabled:opacity-50">
            {scoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {p.score == null ? "Score with AI" : "Re-Score"}
          </button>
          <button onClick={remove} className="inline-flex items-center gap-2 border border-slate-700 hover:border-rose-500 hover:text-rose-400 text-slate-300 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2.5">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Scoring banner */}
      <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-6">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="tick-label mb-2">Prospect Score</div>
          <div className="flex items-baseline gap-2">
            <span className="metric-number text-4xl text-sky-400" data-testid="score-value">{p.score ?? "—"}</span>
            <span className="font-mono text-xs text-slate-500">/100</span>
          </div>
          {p.grade && <div className={`inline-block mt-2 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border ${GRADE_BG[p.grade]}`} data-testid="grade-badge">Grade {p.grade}</div>}
        </div>
        <div className="bg-[var(--bg-secondary)] p-5 md:col-span-2">
          <div className="tick-label mb-2 text-sky-400">/ Rationale</div>
          <p className="text-sm text-slate-200 leading-relaxed">{p.score_rationale || <span className="italic text-slate-500">Score this prospect to generate rationale.</span>}</p>
          {p.score_factors && Object.keys(p.score_factors).length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3 font-mono text-[10px]">
              {Object.entries(p.score_factors).map(([k, v]) => (
                <div key={k} className="text-slate-400">{k.replace(/_/g, " ")}: <span className="text-sky-400">{v}/10</span></div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Intelligence Report (additive) */}
      <div className="panel p-5 mb-6" data-testid="intel-report">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="tick-label text-sky-400 mb-1">/ Intelligence Report</div>
            <div className="font-mono text-[10px] text-slate-500">Priority + sales playbook · does not affect CRM grade</div>
          </div>
          <button onClick={generateIntel} disabled={intelLoading} data-testid="intel-generate-btn" className="inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 disabled:opacity-50">
            {intelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
            {p.intelligence_report ? "Re-Generate" : "Generate Report"}
          </button>
        </div>

        {!p.intelligence_report && (
          <div className="font-mono text-xs text-slate-500 italic py-4">No intelligence report yet. Click Generate to produce one.</div>
        )}

        {p.intelligence_report && (
          <div className="space-y-5" data-testid="intel-report-body">
            {/* Priority headline */}
            <div className="bg-[var(--bg-secondary)] p-4 border border-[var(--border-soft)]">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="tick-label mb-2">Priority</div>
                  <span className={`font-mono text-xs tracking-widest uppercase px-3 py-1.5 border ${PRIORITY_BG[p.intelligence_report.priority] || PRIORITY_BG.Nurture}`} data-testid="intel-priority">{p.intelligence_report.priority}</span>
                </div>
                {!p.intelligence_report.qualified && (
                  <div className="text-xs text-rose-300 font-mono">DISQUALIFIED: {p.intelligence_report.rejection_reason || "Insufficient federal contractor signals."}</div>
                )}
              </div>
            </div>

            {/* Reasons + Risks */}
            <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)]">
              <div className="bg-[var(--bg-secondary)] p-4">
                <div className="tick-label text-emerald-400 mb-2 flex items-center gap-2"><Target className="w-3 h-3" />/ Top 5 Reasons</div>
                <ol className="space-y-1.5 text-sm text-slate-200 list-decimal list-inside" data-testid="intel-reasons">
                  {(p.intelligence_report.top_reasons || []).map((r, i) => (
                    <li key={i} className="leading-snug">{r}</li>
                  ))}
                </ol>
              </div>
              <div className="bg-[var(--bg-secondary)] p-4">
                <div className="tick-label text-rose-400 mb-2 flex items-center gap-2"><AlertTriangle className="w-3 h-3" />/ Top 3 Risks</div>
                <ol className="space-y-1.5 text-sm text-slate-200 list-decimal list-inside" data-testid="intel-risks">
                  {(p.intelligence_report.top_risks || []).map((r, i) => (
                    <li key={i} className="leading-snug">{r}</li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Outreach + Next Action + DM */}
            <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)]">
              <div className="bg-[var(--bg-secondary)] p-4">
                <div className="tick-label text-sky-400 mb-2">/ Recommended Outreach Angle</div>
                <p className="text-sm text-slate-200 leading-relaxed" data-testid="intel-angle">{p.intelligence_report.outreach_angle || <span className="italic text-slate-500">—</span>}</p>
              </div>
              <div className="bg-[var(--bg-secondary)] p-4">
                <div className="tick-label text-sky-400 mb-2 flex items-center gap-2"><ArrowRight className="w-3 h-3" />/ Recommended Next Action</div>
                <p className="text-sm text-slate-200 leading-relaxed" data-testid="intel-action">{p.intelligence_report.next_action || <span className="italic text-slate-500">—</span>}</p>
              </div>
              <div className="bg-[var(--bg-secondary)] p-4">
                <div className="tick-label text-sky-400 mb-2 flex items-center gap-2"><UserCheck className="w-3 h-3" />/ Recommended Decision Maker</div>
                <p className="text-sm text-slate-200 leading-relaxed" data-testid="intel-dm">{p.intelligence_report.decision_maker_to_contact || <span className="italic text-slate-500">—</span>}</p>
              </div>
            </div>

            <div className="font-mono text-[10px] text-slate-600">Generated {p.intelligence_report.generated_at?.slice(0, 19).replace("T", " ")}</div>
          </div>
        )}
      </div>

      {/* Stage management */}
      <div className="panel p-5 mb-6">
        <div className="tick-label text-sky-400 mb-3">/ Pipeline Stage · Currently: <span className="text-slate-100">{stageLabel(p.stage)}</span></div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {STAGES.map((s) => (
            <button key={s} onClick={() => changeStage(s)} data-testid={`stage-${s.replace(/\s+/g, "-")}`} className={`font-mono text-[10px] tracking-widest uppercase px-2.5 py-1.5 border transition-colors ${s === p.stage ? "bg-sky-500 text-slate-950 border-sky-500" : "border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-300"}`}>
              {stageLabel(s)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="tick-label">Next follow-up:</span>
          <input type="date" value={followDate} onChange={(e) => setFollowDate(e.target.value)} className="bg-[var(--bg-primary)] border border-slate-700 text-slate-100 text-sm px-2 py-1.5 font-mono" data-testid="follow-up-date" />
          <span className="text-slate-500">(set then click target stage)</span>
        </div>
      </div>

      {/* Profile + qualification + activities */}
      <div className="grid lg:grid-cols-3 gap-px bg-[var(--border-soft)]">
        <div className="bg-[var(--bg-secondary)] p-5 lg:col-span-2 space-y-3">
          <div className="tick-label text-sky-400 mb-2">/ Intelligence</div>
          {[
            ["Website", p.website], ["Address", p.address], ["Primary NAICS", p.primary_naics], ["Secondary NAICS", p.secondary_naics],
            ["UEI", p.uei], ["CAGE", p.cage],
            ["Certifications", (p.certifications || []).join(", ")], ["Agency Focus", (p.agency_focus || []).join(", ")],
            ["Recent Awards", (p.recent_awards || []).join(", ")], ["Estimated Size", p.estimated_size],
            ["Decision Maker", `${p.decision_maker_name || "—"}${p.decision_maker_title ? " · " + p.decision_maker_title : ""}`],
            ["Email", p.decision_maker_email], ["Phone", p.phone], ["LinkedIn", p.decision_maker_linkedin],
            ["Capability Narrative", p.capability_narrative],
            ["Reason GovBidIQ Helps", p.reason_govbidiq_helps], ["Notes", p.notes],
          ].map(([k, v]) => (
            <div key={k} className="grid grid-cols-12 gap-3 py-1.5 border-b border-[var(--border-soft)] last:border-0">
              <div className="col-span-4 tick-label">{k}</div>
              <div className="col-span-8 text-sm text-slate-200">{v || <span className="text-slate-600">—</span>}</div>
            </div>
          ))}
        </div>

        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="tick-label text-sky-400 mb-3">/ Activity</div>
          <div className="flex gap-2 mb-4">
            <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." className="flex-1 bg-[var(--bg-primary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2 font-mono" data-testid="note-input" />
            <button onClick={addNote} data-testid="note-add" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-2"><Save className="w-3.5 h-3.5" /></button>
          </div>
          <div className="space-y-px bg-[var(--border-soft)] max-h-[480px] overflow-auto">
            {activities.length === 0 && <div className="bg-[var(--bg-secondary)] p-3 text-xs text-slate-500 italic">No activity yet.</div>}
            {activities.map((a) => (
              <div key={a.activity_id} className="bg-[var(--bg-secondary)] p-2.5">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-[10px] text-sky-400 uppercase tracking-widest">{a.activity_type}</span>
                  <span className="font-mono text-[9px] text-slate-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="text-[12px] text-slate-300 mt-0.5">{a.activity_type === "stage_change" ? translateStageDescription(a.description) : a.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
